<template>
  <section>
    <div class="content-wrapper">
      <errors v-if="isVisibleErrorsModal"></errors>

      <div class="content-heading">
        Purchase Community Investment Note
        <small data-localize="dashboard.WELCOME"></small>
      </div>

      <button type="button"
        class="btn-back icon-back bottom-margin"
        v-on:click="goBack()">
        Go back
      </button>

      <div class="row">
        <spinner v-if="yearsRatesLoading || userDetailLoading || isSubmitting"></spinner>

        <div class="col-lg-12">
          <div id="panelChart9" class="panel panel-default">
            <div class="panel-title">
              Purchase Community Investment Note
            </div>

            <div class="line-separator"></div>

            <div class="space" />

            <div class="row top-margin">
              <div class="col-md-6">
                <label>Enter investment amount <strong class="text-info">*</strong></label>
              </div>

              <div class="col-md-6">
                <label>Select rate and term <strong class="text-info">*</strong></label>
              </div>
            </div>

            <div class="row">
              <div class="col-md-6">
                <div class="form-group">
                  <input v-on:change="removeNegative"
                    v-money="money"
                    v-model="investmentAmount"
                    autocomplete="off"
                    name="investmentAmount"
                    id="investmentAmount"
                    :class="{ 'form-control text-right input-md': true, 'form-control-error': isInvestmentAmountInvalid }"
                    v-validate="{required: true}"
                    v-on:blur="validateInvestmentAmount()">
                  <span v-if="isInvestmentAmountInvalid" class="error-feedback">{{investmentAmountError}}</span>
                </div>
              </div>

              <div>
                <CustomRateAndTerm
                  :isCurrentRateAndTerm="true"
                  :mainContainer="''"
                  :rateLimit="investmentsRestrictions.rateLimit"
                  :showDefaultValue="true"
                  :predefinedRatesTerms="interestYearRates"
                  @rateAndTermIsInvalid="rateAndTermIsInvalid"
                  @rateAndTermIsValid="rateAndTermIsValid"
                  @rateChanged="rateChanged"
                  @termChanged="termChanged"
                   />
              </div>
            </div>

            <div class="space" />

            <div class="row top-margin">
              <div class="col-md-6">
                <label>Select bank account <strong class="text-info">*</strong></label>
              </div>

              <div class="col-md-5">
                <label>Date funded <strong class="text-info">*</strong></label>
              </div>
            </div>

            <div class="row">
              <div class="col-md-6">
                <div class="form-group">
                  <select
                    v-model="fundingSourceSelected"
                    name="fundingSource"
                    id="fundingSource"
                    required
                    :class="{ 'form-control input-md': true, 'form-control-error': errors.has('fundingSource') }"
                    v-validate="{required: true}">
                    <option value="" disabled selected hidden>Select bank account</option>
                    <option v-for="(fundingSource, index) in fundingSources"
                      :value="fundingSource.type" :key="index">{{fundingSource.name}}</option>
                  </select>

                  <span v-if="errors.has('fundingSource')" class="error-feedback">Required</span>
                </div>
              </div>

              <div class="col-md-6">
                <div class="form-group">
                  <div class="input-group date">
                    <date-picker
                      v-model="dateFunded"
                      id="dateFunded"
                      placeholder="MM/DD/YYYY"
                      :config="dateConfig"
                      name="dateFunded"
                      :wrap="true"
                      autocomplete="off"
                      :class="{ 'form-control input-md': true, 'form-control-error': isDateFundedInvalid }"
                      v-validate="{required: true}"
                      @dp-change="validateDateFunded">
                    </date-picker>

                    <div class="input-group-addon">
                      <span class="fa fa-calendar"></span>
                    </div>
                  </div>
                  <span v-if="isDateFundedInvalid" class="error-feedback">{{dateFundedError}}</span>
                </div>
              </div>
            </div>

            <div class="space" />

            <div v-if="fundingSourceSelected === 'brokerage'">
              <div class="row top-margin">
                <div class="col-md-6">
                  <label>Distributor fee*</label>
                </div>

                <div class="col-md-6">
                  <label>Brokerage fee*</label>
                </div>
              </div>

              <div class="row">
                <div class="col-md-6">
                  <input v-on:change="removeNegative"
                    v-money="money"
                    id="distributorFee"
                    v-model="distributorFee"
                    autocomplete="off"
                    required
                    class="form-control text-right" />
                </div>

                <div class="col-md-6">
                  <input v-on:change="removeNegative"
                    v-money="money"
                    id="brokerageFee"
                    v-model="brokerageFee"
                    autocomplete="off"
                    required
                    class="form-control text-right" />
                </div>
              </div>
            </div>

            <div class="space" />

            <div v-if="fundingSourceSelected === 'brokerage'">
              <div class="row top-margin">
                <div class="col-md-6">
                  <label>CUSIP*</label>
                </div>
              </div>

              <div class="row">
                <div class="col-md-6">
                  <input type="text"
                    name="cusip"
                    id="cusip"
                    v-model="cusip"
                    class="form-control"
                    v-mask="'*********'"
                    v-on:blur="validateCusip()"
                    :class="{ 'form-control': true, 'form-control-error': isCusipInvalid }"
                    v-validate="{required: true}" />
                  <span v-if="isCusipInvalid" class="error-feedback">Please input 9 characters in order to proceed.</span>
                </div>
              </div>
            </div>

            <div class="space" />

            <div class="row top-margin">
              <div class="col-md-10">
                <button type="submit"
                  class="btn btn-info btn-lg button-approve"
                  v-bind:disabled="isSubmitButtonDisabled()"
                  v-on:click="submitRequest()">
                  Submit request
                </button>
              </div>
            </div>

            <div class="line-separator top-margin"></div>

            <button type="button"
              class="btn-back icon-back bottom-margin"
              v-on:click="goBack()">
              Go back
            </button>

            <div class="space" />
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
  import {
    mapActions,
    mapMutations,
    mapState,
  } from 'vuex';

  import Errors from '@/components/modals/Errors';
  import {
    VMoney,
  } from 'v-money';

  import moment from 'moment';

  import {
    SET_ERRORS_MODAL_INFO,
    SHOW_ERRORS_MODAL,
  } from '@/store/mutation-types';

  import {
    formatAmount,
    formatDecimal,
    findAccount,
  } from '@/util';
  import spinner from '@/components/widgets/Spinner';
  import Question from '@/components/modals/Question';
  import CustomRateAndTerm from '@/components/CustomRateAndTerm';
  import { Constants } from '@/data/static/constants';
  import { env } from '@/config/config';

  const mix = {
    methods: {
      formatAmount,
      formatDecimal,
      findAccount,
    },
  };

  export default {
    name: 'purchaseInvestmentNote',
    beforeMount() {
      this.setParams();
      this.loadYearsRates();
      this.loadFundingSources();
      this.loadInvestmentsValidations();
    },
    mixins: [mix],
    components: {
      errors: Errors,
      spinner,
      question: Question,
      CustomRateAndTerm,
    },
    data() {
      return {
        name: '',
        userId: '',
        accountId: '',
        userAccount: {},
        investmentAmount: 0,
        distributorFee: 0,
        brokerageFee: 0,
        cusip: '',
        interestYearRates: [],
        fundingSources: [],
        fundingSourceSelected: '',
        yearsRatesLoading: false,
        userDetailLoading: false,
        isInvestmentAmountInvalid: false,
        isCusipInvalid: false,
        investmentAmountError: '',
        isDateFundedInvalid: false,
        minimumInvestmentAmount: null,
        rate: 0,
        term: 1,
        isRateAndTermValid: true,
        dateFunded: '',
        dateFormat: 'YYYY-MM-DD',
        dateConfig: {
          format: 'MM/DD/YYYY',
        },
        money: {
          decimal: '.',
          thousands: ',',
          prefix: '$',
          precision: 2,
        },
        isSubmitting: false,
        dateFundedInThePastError: false,
        dateFundedInTheFutureError: false,
      };
    },
    computed: {
      ...mapState('errors', [
        'isVisibleErrorsModal',
      ]),
      ...mapState('app', [
        'fundingSourceTypes',
        'investmentsRestrictions',
        'investmentsRestrictions',
      ]),
      dateFundedError() {
        return this.dateFundedInThePastError ? 'The date funded cannot be more than 10 days in the past' : 'Please input a past or present date.';
      },
      isBrokerageEnabled() {
        const { brokerageEnabledAccountIds } = Constants.purchaseInvestmentNote;
        const enabledAccountIds = env === 'production'
          ? brokerageEnabledAccountIds.production
          : brokerageEnabledAccountIds.default;
        return enabledAccountIds.includes(String(this.accountId));
      },
    },
    directives: {
      money: VMoney,
    },
    methods: {
      ...mapActions('note', [
        'getInterestYearRates',
        'purchaseNote',
      ]),
      ...mapActions('accountCreation', [
        'getUserDetail',
      ]),
      ...mapMutations('errors', {
        showErrorsModal: SHOW_ERRORS_MODAL,
        setErrorsModalInfo: SET_ERRORS_MODAL_INFO,
      }),
      setParams() {
        this.userId = this.$route.params.userId;
        this.name = this.$route.params.name;
        this.accountId = this.$route.params.accountId;
      },
      loadYearsRates() {
        this.interestYearRates = this.investmentsRestrictions.ratesAndTerms;
      },
      loadFundingSources() {
        this.fundingSources = [{
          name: this.fundingSourceTypes.CHECK.label,
          type: this.fundingSourceTypes.CHECK.name,
        }, {
          name: this.fundingSourceTypes.WIRE_TRANSFER.label,
          type: this.fundingSourceTypes.WIRE_TRANSFER.name,
        }];

        if (this.isBrokerageEnabled) {
          this.fundingSources.push({
            name: this.fundingSourceTypes.BROKERAGE.label,
            type: this.fundingSourceTypes.BROKERAGE.name,
          });
        }
      },
      loadUserDetail() {
        this.userDetailLoading = true;
        return this.getUserDetail(this.userId).then((user) => {
          this.userDetailLoading = false;
          this.userAccount = this.findAccount(user.accounts, this.accountId);
          this.fundingSourceSelected = this.userAccount.defaultFundingSourceId;
        }).catch((err) => {
          this.userDetailLoading = false;
          if (err !== 'UNAUTHORIZED') {
            this.setErrorsModalInfo(['We could not retrieve user detail']);
            this.showErrorsModal();
          }
        });
      },
      loadInvestmentsValidations() {
        this.minimumInvestmentAmount = this.investmentsRestrictions.minimumAmount;
        this.investmentAmountError = `Please invest at least ${formatAmount(parseInt(this.minimumInvestmentAmount, 10))} in order to proceed`;
      },
      removeNegative() {
        this.investmentAmount = this.investmentAmount.toString().replace('-', '');
        this.distributorFee = this.distributorFee.toString().replace('-', '');
        this.brokerageFee = this.brokerageFee.toString().replace('-', '');
      },
      validateInvestmentAmount() {
        const investmentAmountValue = this.getUnformattedValue(this.investmentAmount);
        if (investmentAmountValue < this.minimumInvestmentAmount) {
          this.isInvestmentAmountInvalid = true;
        } else {
          this.isInvestmentAmountInvalid = false;
        }
      },
      validateCusip() {
        if (!this.cusip || this.cusip.includes('_')) {
          this.isCusipInvalid = true;
        } else {
          this.isCusipInvalid = false;
        }
      },
      validateDateFunded() {
        const dateFunded = moment(this.dateFunded);
        const today = moment();
        const dayDifference = dateFunded.diff(today, 'days');
        this.dateFundedInThePastError = dayDifference < -10;
        this.dateFundedInTheFutureError = dateFunded > today;
        this.isDateFundedInvalid = this.dateFundedInThePastError || this.dateFundedInTheFutureError;
      },
      rateChanged(newRate) {
        this.rate = newRate;
      },
      termChanged(newTerm) {
        this.term = newTerm;
      },
      isSubmitButtonDisabled() {
        const investmentAmountValue = this.getUnformattedValue(this.investmentAmount);
        if (investmentAmountValue < this.minimumInvestmentAmount) {
          return true;
        }
        if (!this.fundingSourceSelected) {
          return true;
        }
        if (!this.dateFunded || this.isDateFundedInvalid) {
          return true;
        }
        const brokerageOption = this.fundingSources
          .find(fs => fs.type === this.fundingSourceTypes.BROKERAGE.name);
        if (brokerageOption && this.fundingSourceSelected === brokerageOption.name &&
          (!this.cusip || this.cusip.includes('_'))) {
          return true;
        }

        if (!this.isRateAndTermValid) {
          return true;
        }
        return false;
      },
      getUnformattedValue(value) {
        const unformattedValue = value.toString().replace('$', '').replace(/,/g, '');
        return this.formatDecimal(unformattedValue);
      },
      submitRequest() {
        const purchaseObject = {
          userId: this.userId,
          investmentAmount: this.getUnformattedValue(this.investmentAmount).toFixed(2),
          rate: this.rate,
          term: this.term,
          fundingSourceType: this.fundingSourceSelected,
          dateFunded: moment(this.dateFunded).format(this.dateFormat).toString(),
          distributorFee: this.getUnformattedValue(this.distributorFee).toFixed(2),
          brokerageFee: this.getUnformattedValue(this.brokerageFee).toFixed(2),
          cusip: this.cusip,
        };

        this.isSubmitting = true;

        this.purchaseNote({
          data: {
            accountId: this.accountId,
            ...purchaseObject,
          },
        })
        .then(() => {
          this.isSubmitting = false;
          this.goBack();
        })
        .catch(() => {
          this.isSubmitting = false;
          this.setErrorsModalInfo(['Something went wrong. Please try again.']);
          this.showErrorsModal();
        });
      },
      rateAndTermIsInvalid() {
        this.isRateAndTermValid = false;
      },
      rateAndTermIsValid() {
        this.isRateAndTermValid = true;
      },
      goBack() {
        this.$router.go(-1);
      },
    },
  };
</script>

<style scoped>
  .line-separator {
    padding: 8px;
    line-height: 1.52857143;
    vertical-align: top;
    border-top: 1px solid #eee;
  }

  .left-margin {
    margin-left: 4%;
  }

  .right-margin {
    margin-left: -4%;
  }

  .top-margin {
    margin-top: 2%;
  }

  .bottom-margin {
    margin-bottom: 1%;
  }

  .space {
    margin-top: 4%;
  }

  .content-heading {
    font-weight: normal;
  }

  .panel-title {
    font-size: 20px;
  }

  .panel-subtitle {
    font-size: 16px;
  }

  .panel-default {
    padding: 30px 40px;
    width: 100%;
  }

  .panel-heading {
    background-color: #ffffff;
    font-size: 16px;
    padding: 0px 5px;
  }

  label {
    color: #888;
    font-size: 14px;
    width: 250px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin-bottom: 0px;
  }

  select:required:invalid {
    color: lightgray;
  }

  option[value=""][disabled] {
    display: none;
  }

  option {
    color: black;
  }

  .total-amount {
    font-size: 20px;
  }

  .button-approve {
    background-color: #127285;
  }

  .button-white {
    background-color: white;
    border-color: #127285;
    color: #127285;
  }

  .btn-back {
    color: #31b0d5;
    background-color: Transparent;
    background-repeat: no-repeat;
    border: none;
    cursor: pointer;
    overflow: hidden;
    outline: none;
    padding-left: 20px;
  }

  .icon-back {
    position: relative;
  }

  .icon-back:before {
    content: "\f060";
    font-family: FontAwesome;
    position: absolute;
    left: 0px;
  }

   ::placeholder {
    /* Chrome, Firefox, Opera, Safari 10.1+ */
    color: lightgray;
    opacity: 1;
    /* Firefox */
  }

  :-ms-input-placeholder {
    /* Internet Explorer 10-11 */
    color: lightgray;
  }

  ::-ms-input-placeholder {
    /* Microsoft Edge */
    color: lightgray;
  }

  .left-padding {
    padding-left: 0;
  }
</style>



// WEBPACK FOOTER //
// PurchaseInvestmentNote.vue?1cd46114